// frontend/src/pages/DataWorkspacePage/DataWorkspacePage.jsx
import React, { useState, useCallback } from 'react';
import ConnectionsSidebar from './ConnectionsSidebar';
import GridEditor from './GridEditor';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';

/**
 * Unified Data Workspace — main layout page.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  Header (breadcrumb + toolbar)                       │
 *   ├─────────────┬────────────────────────────────────────┤
 *   │  Sidebar    │  Main viewport (Grid / SQL editor)     │
 *   │  (20%)      │  (80%)                                 │
 *   └─────────────┴────────────────────────────────────────┘
 */

const DataWorkspacePage = ({ token, currentUser }) => {
    const { addNotification } = useNotification();

    // Active table / connection selection from sidebar
    const [activeSelection, setActiveSelection] = useState(null);
    // { connectionId, tableName, schemaName }

    // Data returned by proxy
    const [gridData, setGridData] = useState(null);
    // { columns: [], rows: [], total: 0, page: 1, page_size: 200 }

    const [isLoading, setIsLoading] = useState(false);

    // -----------------------------------------------------------------------
    // Fetch table data (called by sidebar click or GridEditor pagination)
    // -----------------------------------------------------------------------
    const fetchTableData = useCallback(async (selection, page = 1, pageSize = 200, sortCol = null, sortDir = 'asc', filters = []) => {
        if (!selection?.connectionId || !selection?.tableName) return;
        setIsLoading(true);
        try {
            const result = await apiFetch(
                `/data-workspace/connections/${selection.connectionId}/query/table`,
                token,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        table_name:  selection.tableName,
                        schema_name: selection.schemaName || null,
                        page,
                        page_size:   pageSize,
                        sort_col:    sortCol,
                        sort_dir:    sortDir,
                        filters,
                    }),
                }
            );
            setGridData(result);
        } catch (err) {
            addNotification(`Failed to load table: ${err.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    }, [token, addNotification]);

    const handleSelectTable = useCallback((selection) => {
        setActiveSelection(selection);
        setGridData(null);
        fetchTableData(selection);
    }, [fetchTableData]);

    const handlePageChange = useCallback((page, pageSize) => {
        fetchTableData(activeSelection, page, pageSize);
    }, [activeSelection, fetchTableData]);

    return (
        <div className="flex h-screen overflow-hidden bg-[#0a0f1e]">
            {/* ── Sidebar ─────────────────────────────────────────────────── */}
            <ConnectionsSidebar
                token={token}
                onSelectTable={handleSelectTable}
                activeSelection={activeSelection}
            />

            {/* ── Main Viewport ────────────────────────────────────────────── */}
            <div className="flex flex-col flex-1 overflow-hidden">
                {/* Header bar */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-[#0d1526] shrink-0">
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                        <DynamicIcon name="Database" className="w-4 h-4 text-blue-400" />
                        {activeSelection ? (
                            <>
                                <span className="text-slate-500">{activeSelection.connectionName}</span>
                                <span className="text-slate-600">/</span>
                                <span className="text-slate-300 font-semibold">{activeSelection.tableName}</span>
                                {gridData && (
                                    <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300 border border-blue-800">
                                        {gridData.total.toLocaleString()} rows
                                    </span>
                                )}
                            </>
                        ) : (
                            <span className="text-slate-500 italic">Select a table from the sidebar to begin</span>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        {activeSelection && (
                            <button
                                onClick={() => fetchTableData(activeSelection)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/40 transition-colors"
                            >
                                <DynamicIcon name="RefreshCw" className="w-3 h-3" />
                                Refresh
                            </button>
                        )}
                    </div>
                </div>

                {/* Grid Area */}
                <div className="flex-1 overflow-hidden">
                    {!activeSelection ? (
                        <EmptyState />
                    ) : (
                        <GridEditor
                            token={token}
                            connectionId={activeSelection.connectionId}
                            tableName={activeSelection.tableName}
                            schemaName={activeSelection.schemaName}
                            gridData={gridData}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-full text-center p-12">
        <div className="relative mb-6">
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-600/20 to-indigo-600/20 border border-blue-500/20 flex items-center justify-center">
                <DynamicIcon name="DatabaseZap" className="w-12 h-12 text-blue-400/60" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600/40 to-purple-600/40 border border-indigo-500/30 flex items-center justify-center">
                <DynamicIcon name="Table" className="w-4 h-4 text-indigo-400" />
            </div>
        </div>
        <h3 className="text-xl font-semibold text-slate-200 mb-2">Unified Data Workspace</h3>
        <p className="text-slate-500 max-w-sm text-sm leading-relaxed">
            Connect to PostgreSQL, MySQL, or MSSQL databases, browse tables, and edit data with full write-back support.
        </p>
        <div className="mt-6 flex gap-3 text-xs text-slate-600">
            <span className="flex items-center gap-1"><DynamicIcon name="ArrowLeft" className="w-3 h-3" /> Select a table from the left sidebar</span>
        </div>
    </div>
);

export default DataWorkspacePage;
